import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarRange, ClipboardCheck, AlertTriangle, ArrowRight } from 'lucide-react';

const STATE_STYLE = {
  completed: { chip: 'bg-blue-100 text-blue-700 border-blue-200', bar: 'bg-blue-400', label: 'Completed' },
  current: { chip: 'bg-emerald-100 text-emerald-700 border-emerald-200', bar: 'bg-emerald-500', label: 'In progress' },
  upcoming: { chip: 'bg-gray-100 text-gray-600 border-gray-200', bar: 'bg-gray-300', label: 'Upcoming' },
};

/** Horizontal strip of phase cards shown on the cycle Overview tab. */
export const PhaseTimeline = ({ phases, cycleId }) => {
  const navigate = useNavigate();
  if (!phases || phases.length === 0) return null;
  return (
    <div data-testid="phase-timeline" className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarRange className="w-4 h-4 text-ica-navy" />
          <h3 className="font-semibold text-sm text-ica-navy">Phases ({phases.length})</h3>
        </div>
        <span className="text-[11px] text-gray-500">Click a phase to drill down</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {phases.map((p, i) => {
          const st = STATE_STYLE[p.state] || STATE_STYLE.upcoming;
          const total = p.assessments_total || 0;
          const pct = total > 0 ? Math.round(((p.assessments_submitted || 0) / total) * 100) : 0;
          return (
            <button
              type="button"
              key={p.id}
              data-testid={`phase-card-${i}`}
              onClick={() => navigate(`/cycles/${cycleId}/phases/${p.id}`)}
              className="relative text-left rounded-lg border border-gray-200 bg-white p-3 overflow-hidden group hover:border-ica-navy/40 hover:shadow-sm transition-[border-color,box-shadow]"
            >
              <div className={`absolute left-0 top-0 bottom-0 w-1 ${st.bar}`} />
              <div className="flex items-start justify-between gap-2 pl-2">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-gray-500">Phase {i + 1}</div>
                  <div className="font-semibold text-sm text-ica-navy truncate inline-flex items-center gap-1">
                    {p.name} <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-70 transition-opacity" />
                  </div>
                  <div className="text-[11px] text-gray-600 mt-0.5">{p.start_date} → {p.end_date}</div>
                </div>
                <span className={`shrink-0 inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st.chip}`}>{st.label}</span>
              </div>
              <div className="pl-2 mt-2 flex items-center justify-between text-[11px]">
                <span className="inline-flex items-center gap-1 text-gray-700">
                  <ClipboardCheck className="w-3 h-3" /> {p.num_assessments} assessment{p.num_assessments === 1 ? '' : 's'}
                  {total > 0 && <span className="text-gray-500"> · {p.assessments_submitted}/{total} submitted</span>}
                </span>
                {p.assessments_overdue > 0 && (
                  <span className="inline-flex items-center gap-0.5 text-red-600 font-medium"><AlertTriangle className="w-3 h-3" /> {p.assessments_overdue} overdue</span>
                )}
              </div>
              {total > 0 && (
                <div className="ml-2 h-1 bg-gray-200 rounded-full mt-1.5 overflow-hidden">
                  <div className={`h-full ${st.bar} transition-[width] duration-500`} style={{ width: `${pct}%` }} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default PhaseTimeline;
